import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Search, Loader2, RefreshCw, Trash2, X, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const BulkResearchCard = React.memo(function BulkResearchCard() {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [bulkJob, setBulkJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await axios.get(`${API}/admin/research/stats`);
      setStats(res.data);
    } catch { /* ignore */ }
    setLoadingStats(false);
  }, []);

  const fetchJobStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/research/bulk-status`);
      setBulkJob(res.data);
      return res.data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchJobStatus();
  }, [fetchStats, fetchJobStatus]);

  // Poll while job is running
  useEffect(() => {
    if (!bulkJob || bulkJob.status !== "running") return;
    const interval = setInterval(async () => {
      const job = await fetchJobStatus();
      if (job && job.status !== "running") {
        fetchStats();
        if (job.status === "completed") toast.success(`Bulk research completed: ${job.completed} leads`);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [bulkJob, fetchJobStatus, fetchStats]);

  const handleResearchMissing = async () => {
    setStarting(true);
    try {
      const res = await axios.post(`${API}/admin/research/bulk`);
      if (res.data.queued === 0) {
        toast.info(res.data.message || "All leads already researched");
      } else {
        toast.success(`Queued ${res.data.queued} leads for research`);
        setBulkJob({ status: "running", total: res.data.queued, completed: 0, failed: 0 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start bulk research");
    }
    setStarting(false);
  };

  const handleResearchAll = async () => {
    setConfirmResetOpen(false);
    setStarting(true);
    try {
      const res = await axios.post(`${API}/admin/research/bulk-all`);
      toast.success(`Cleared ${res.data.cleared} old records. Queued ${res.data.queued} leads.`);
      setBulkJob({ status: "running", total: res.data.queued, completed: 0, failed: 0 });
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start");
    }
    setStarting(false);
  };

  const handleCancel = async () => {
    try {
      await axios.post(`${API}/admin/research/bulk-cancel`);
      toast.success("Bulk research cancelled");
      fetchJobStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const isRunning = bulkJob?.status === "running";
  const progress = isRunning && bulkJob.total > 0
    ? Math.round((bulkJob.completed + (bulkJob.failed || 0)) / bulkJob.total * 100)
    : 0;

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4 text-purple-600" />
            Lead Research
          </CardTitle>
          <CardDescription>Run AI-powered web research on leads to build sales profiles</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats row */}
          {stats && (
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-slate-800">{stats.total_leads}</p>
                <p className="text-[10px] text-slate-500 uppercase font-medium">Total Leads</p>
              </div>
              <div className="bg-green-50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-green-700">{stats.researched}</p>
                <p className="text-[10px] text-green-600 uppercase font-medium">Researched</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-amber-700">{stats.not_researched}</p>
                <p className="text-[10px] text-amber-600 uppercase font-medium">Missing</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-purple-700">{stats.old_format}</p>
                <p className="text-[10px] text-purple-600 uppercase font-medium">Old Format</p>
              </div>
            </div>
          )}
          {loadingStats && !stats && (
            <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          )}

          {/* Running job progress */}
          {isRunning && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                  <span className="text-sm font-medium text-purple-700">Research in progress</span>
                </div>
                <Button variant="ghost" size="sm" onClick={handleCancel} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2 text-xs">
                  <X className="h-3 w-3 mr-1" /> Cancel
                </Button>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-2">
                <div className="bg-purple-600 h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <div className="flex items-center justify-between text-xs text-purple-600">
                <span>{bulkJob.completed}/{bulkJob.total} completed{bulkJob.failed > 0 ? ` · ${bulkJob.failed} failed` : ''}</span>
                {bulkJob.current_lead && <span className="text-purple-400 truncate ml-2">Current: {bulkJob.current_lead}</span>}
              </div>
            </div>
          )}

          {/* Last job result */}
          {bulkJob && bulkJob.status !== "running" && (
            <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${bulkJob.status === 'completed' ? 'bg-green-50 text-green-700' : bulkJob.status === 'cancelled' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
              {bulkJob.status === "completed" ? <CheckCircle2 className="h-3.5 w-3.5" /> : bulkJob.status === "cancelled" ? <Clock className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
              <span>Last job: {bulkJob.status} — {bulkJob.completed}/{bulkJob.total} leads{bulkJob.failed > 0 ? ` (${bulkJob.failed} failed)` : ''}</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleResearchMissing}
              disabled={isRunning || starting}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
            >
              {starting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}
              Research Missing Leads
              {stats && stats.not_researched > 0 && (
                <Badge variant="secondary" className="ml-2 bg-purple-500 text-white text-[10px] px-1.5">{stats.not_researched}</Badge>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setConfirmResetOpen(true)}
              disabled={isRunning || starting}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Reset & Re-run All
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { fetchStats(); fetchJobStatus(); }}
              disabled={loadingStats}
              className="shrink-0"
            >
              <RefreshCw className={`h-4 w-4 ${loadingStats ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation dialog for reset all */}
      <AlertDialog open={confirmResetOpen} onOpenChange={setConfirmResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset & Re-run All Research?</AlertDialogTitle>
            <AlertDialogDescription>
              This will <strong>delete all existing research data</strong> for every lead and re-run research from scratch.
              This can take a long time for large lead databases. Each lead takes ~30-60 seconds.
              {stats && <span className="block mt-1 font-medium">Estimated time: ~{Math.round(stats.total_leads * 0.75)} minutes for {stats.total_leads} leads.</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResearchAll} className="bg-red-600 hover:bg-red-700">
              Yes, Reset & Re-run All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

export default BulkResearchCard;
